// Lab Analysis visit type (Beluga `visitType: labAnalysis`, Labs: Yes,
// Follow-up: None). A general-wellness lab-testing visit with a real Q/A
// questionnaire + provider FEEDBACK on results — distinct from the existing
// labTestingAutoAuthorization ($299 60-biomarker auto-approved panel, see
// labPanelData.js). No medication, no pharmacy: a one-time lab-order charge,
// relayed via /beluga/create-visit-v2 like the other Q/A visit types.
// Source questionnaire: "Lab Analysis Questionnaire" (Beluga, 2026-07).
// BE contract: docs/BACKEND_LAB_ANALYSIS_VISIT_PRD.md.

// ⚠️ PLACEHOLDER PRICE — confirm with product/Karina before public launch.
// The displayed price must equal the Square charge; kept as a single constant
// so it's a one-line swap (mirrors labPanelData's LAB_PANEL_PRICE).
export const LAB_ANALYSIS_PRICE = 99;
export const LAB_ANALYSIS_NAME = 'Wellness Lab Analysis';
export const LAB_ANALYSIS_PLAN_SLUG = 'lab-analysis';

export const LAB_ANALYSIS_WELLNESS_CONSENT_BODY = `Consent (General Wellness Lab Testing and Feedback):

You are requesting laboratory testing as part of a general wellness screening. The purpose of this testing is to provide information about your overall health and identify potential areas for further evaluation or optimization.

By proceeding, you acknowledge and understand the following:

Purpose of Testing: These tests are intended for general wellness purposes only. They are not designed to diagnose, treat, or monitor any specific medical condition. This encounter is NOT appropriate if you currently are experiencing symptoms.

Scope of Encounter: This encounter is not a substitute for in-person medical evaluation, diagnosis, or management by your primary care provider or specialist. Any findings should be reviewed with your regular healthcare provider, especially if you have ongoing symptoms, medical conditions, or concerns.

Provider Feedback: You will receive general feedback regarding your lab results. This feedback may include educational insights, lifestyle suggestions, or recommendations to follow up with a healthcare provider for further evaluation.

Limitations: The information provided is based solely on the lab results and the information you provide in your questionnaire. It does not include a comprehensive physical exam, detailed medical history review, or diagnostic workup.

Follow-Up Responsibility: It is your responsibility to follow up with a licensed healthcare provider for any necessary diagnosis, ongoing care, or treatment of abnormal results or symptoms.

By agreeing to proceed, you confirm that you have read and understood the above information, and you consent to have labs ordered and reviewed as part of this general wellness screening.`;

export const LAB_ANALYSIS_TRUTHFULNESS_CONSENT_BODY = `Consent (Truthfulness):

Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.`;

// ---------- Steps ----------
// slide_ids for the medical-history + demographics questions match the ids the
// BE create-visit-v2 mapper already keys on for named Beluga fields
// (medicalConditions, selfReportedMeds, allergies, sex, height, weight) — see
// the BE PRD §2. Everything else rides the Q/A questionnaire section.

const basicContactStep = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send you updates about your lab analysis.",
};

const medicalConditionsStep = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  placeholder: 'e.g., Type 2 Diabetes, Hypertension, etc. (or "None")',
  required: true,
};

const medicationsListStep = {
  id: 'medications-list',
  type: 'textarea',
  question: 'Please list all your current medications including dosages',
  placeholder: 'e.g., Metformin 500mg twice daily (or "None")',
  required: true,
};

const allergiesStep = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  placeholder: 'e.g., Penicillin, Shellfish (or "None")',
  required: true,
};

const sexAtBirthStep = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ],
};

const heightWeightStep = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  skipBmiCheck: true, // wellness lab visit — no BMI eligibility gate
};

const wellnessConsentStep = {
  id: 'wellness-consent',
  type: 'consent-dropdown',
  question: 'Consent — General Wellness Lab Testing and Feedback',
  consentText: LAB_ANALYSIS_WELLNESS_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the above information, I would like to continue', value: 'consent' },
    { label: 'I have read and understand the above information and I would NOT like to continue', value: 'decline', disqualifies: true },
  ],
};

const goalsStep = {
  id: 'lab-goals',
  type: 'multi-select',
  question: 'What are your main goals for this lab testing?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Complete an overall wellness screening', value: 'wellness-screening' },
    { label: 'Check thyroid function', value: 'thyroid' },
    { label: 'Evaluate hormone levels', value: 'hormones' },
    { label: 'Check vitamin or nutrient levels', value: 'vitamins' },
    { label: 'Assess cardiovascular risk (cholesterol, inflammation)', value: 'cardiovascular' },
    { label: 'Review liver or kidney function', value: 'liver-kidney' },
    { label: 'Check complete blood count', value: 'cbc' },
    { label: 'Other', value: 'other' },
  ],
};

const goalsOtherStep = {
  id: 'lab-goals-other',
  type: 'textarea',
  question: 'Please provide more information about your other goal',
  showIf: { field: 'lab-goals', includes: 'other' },
  required: true,
};

const lifestyleStep = {
  id: 'lifestyle',
  type: 'multi-select',
  question: 'How would you describe your current lifestyle?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Sedentary', value: 'sedentary' },
    { label: 'Moderately active', value: 'moderately-active' },
    { label: 'Very active', value: 'very-active' },
    { label: 'High stress', value: 'high-stress' },
    { label: 'Poor sleep quality', value: 'poor-sleep' },
    { label: 'Balanced diet', value: 'balanced-diet' },
    { label: 'Vegetarian or vegan', value: 'vegetarian-vegan' },
    { label: 'Intermittent fasting or specific diet plan', value: 'fasting-diet-plan' },
    { label: 'Trying to conceive or pregnant', value: 'conceive-pregnant' },
  ],
};

const lastLabDateStep = {
  id: 'last-lab-date',
  type: 'single-select',
  question: 'When was your last set of lab tests performed?',
  options: [
    { label: 'Within the last 3 months', value: 'within-3-months' },
    { label: '3–6 months ago', value: '3-6-months' },
    { label: '6–12 months ago', value: '6-12-months' },
    { label: 'Over a year ago', value: 'over-a-year' },
    { label: 'Unsure or never tested', value: 'unsure-never' },
  ],
};

const abnormalResultsStep = {
  id: 'abnormal-results',
  type: 'single-select',
  question: 'Were there any abnormal results in the past?',
  // Only shown when the patient reported a prior lab date (not unsure/never).
  showIf: { field: 'last-lab-date', in: ['within-3-months', '3-6-months', '6-12-months', 'over-a-year'] },
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'Unsure', value: 'unsure' },
  ],
};

const abnormalDescribeStep = {
  id: 'abnormal-describe',
  type: 'textarea',
  question: 'Please describe which results were abnormal',
  showIf: { field: 'abnormal-results', value: 'yes' },
  required: true,
};

const doctorQuestionsStep = {
  id: 'doctor-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Optional — share anything else you would like the provider to know.',
};

// §results-submission (BE PR #225 + #230): labAnalysis is a REVIEW-of-existing-
// results visit. ONE page (LabResultsEntryStep) lets the patient EITHER upload a
// PDF/photo of their lab report — auto-read into rows via /ai/extract-lab-results,
// which they confirm/edit — OR type the rows manually, on the SAME screen (an
// upload button sits above the manual rows; Dhale 2026-08-06 wanted them together,
// not a separate choose-method step). Both produce `results[]` (Continue gated
// >=1 complete row → never empty, which Beluga requires). An uploaded file is ALSO
// relayed post-submit (uploadVisitPhoto, slideId 'lab-results-upload') so the
// provider sees the full document.
const labResultsStep = {
  id: 'lab-results',
  type: 'lab-results',
  question: 'Your lab results',
  subtitle:
    'Upload your lab report and we’ll fill this in for you to check — or enter your results manually. Every field is required.',
};

// Patient address — required by /lab/receiveLabResults. Reuses the /lab-panel
// ShippingAddressStep (answer shape { address1, city, state, zipCode }); mapped
// to contact.address/city/state/zip on submit.
const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Your address',
  subtitle: 'We use this for your lab records and to route your results.',
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle:
    "Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible. If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card. Make sure the photo is clear and legible.",
};

const truthfulnessConsentStep = {
  id: 'truthfulness-consent',
  type: 'consent-dropdown',
  question: 'Consent — Truthfulness',
  consentText: LAB_ANALYSIS_TRUTHFULNESS_CONSENT_BODY,
  options: [
    { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
    { label: 'I have read the above information and I do not wish to continue', value: 'decline', disqualifies: true },
  ],
};

const checkoutStep = { id: 'checkout', type: 'checkout', question: 'Review & pay' };
const completeStep = { id: 'complete', type: 'complete', question: 'Complete' };

// Full ordered flow (conditionals filtered by buildDynamicLabAnalysisFlow).
export const labAnalysisFlow = [
  basicContactStep,
  medicalConditionsStep,
  medicationsListStep,
  allergiesStep,
  sexAtBirthStep,
  heightWeightStep,
  wellnessConsentStep,
  goalsStep,
  goalsOtherStep,
  lifestyleStep,
  lastLabDateStep,
  abnormalResultsStep,
  abnormalDescribeStep,
  doctorQuestionsStep,
  labResultsStep, // upload-or-manual on one page (see LabResultsEntryStep)
  shippingAddressStep,
  identityVerificationStep,
  truthfulnessConsentStep,
  checkoutStep,
  completeStep,
];

export const getLabAnalysisQuestionById = (id) => labAnalysisFlow.find((q) => q.id === id);

// showIf predicates: { field, value } eq · { field, includes } multi-select
// contains · { field, in: [...] } value-in-list. Absent showIf = always shown.
function isVisible(step, answers) {
  const c = step.showIf;
  if (!c) return true;
  const current = answers[c.field];
  if (Array.isArray(c.in)) return c.in.includes(current);
  if (c.includes !== undefined) return Array.isArray(current) && current.includes(c.includes);
  return current === c.value;
}

export const buildDynamicLabAnalysisFlow = (answers = {}) =>
  labAnalysisFlow.filter((step) => isVisible(step, answers));

export const isDisqualifyingAnswer = (questionId, answer) => {
  if ((questionId === 'wellness-consent' || questionId === 'truthfulness-consent') && answer === 'decline') {
    return {
      disqualified: true,
      reason: 'You have indicated that you do not wish to proceed with the wellness lab testing.',
    };
  }
  return { disqualified: false };
};

export default labAnalysisFlow;
